/**
 * ---------------------------------------------------------------------------
 * Block Registry Context
 * ---------------------------------------------------------------------------
 * A run-time registry that lets blocks discover and communicate with each other
 * while the Page Builder is running. It powers three big use-cases:
 *
 *  1. Smooth-scrolling (CTA → Hero) via `registry.scrollTo()`
 *  2. Point-to-point events (accordion.open(), modal.open()) via `dispatch()`
 *  3. Global pub-sub (`publish` / `subscribe`) without React props drilling
 *
 *  The registry is React-context backed and **only** instantiated in the
 *  builder. When blocks are rendered on the public site there is no provider;
 *  components should therefore fall back to native DOM APIs (as the
 *  ImageTextBlock does).
 *
 *  👉  To register a block use the `withBlockRegistration()` HOC or the
 *      `useBlockRegistration()` hook – they handle lifecycle, cleanup and also
 *      ensure the DOM element receives `id="block-<id>"` for public scrolling.
 *
 *  Minimal usage (inside a block component):
 *  ```ts
 *  const ref = useRef<HTMLDivElement>(null);
 *  useBlockRegistration(id, ref, { type: 'hero', label: heading });
 *  ```
 */

import React, { createContext, ReactNode, useContext, useRef } from 'react';

/**
 * Block data stored in the registry
 */
interface BlockData {
    ref: React.RefObject<HTMLElement>;
    type: string;
    label?: string;
    callbacks?: {
        [eventName: string]: (payload?: any) => void;
    };
}

/**
 * Registry entry for list operations
 */
interface BlockListEntry {
    id: string;
    type: string;
    label: string;
}

/**
 * Scroll options for scrollTo method
 */
interface ScrollOptions {
    behavior?: ScrollBehavior;
    block?: ScrollLogicalPosition;
    inline?: ScrollLogicalPosition;
    offset?: number;
}

/**
 * Event handler type for pub-sub
 */
type EventHandler = (payload?: any) => void;

/**
 * Block Registry API
 */
export interface BlockRegistryAPI {
    register: (id: string | number, data: BlockData) => void;
    unregister: (id: string | number) => void;
    get: (id: string | number) => BlockData | undefined;
    list: () => BlockListEntry[];
    scrollTo: (id: string | number, options?: ScrollOptions) => void;
    dispatch: (id: string | number, event: string, payload?: any) => void;
    publish: (event: string, payload?: any) => void;
    subscribe: (event: string, handler: EventHandler) => () => void;
}

/**
 * Internal registry implementation
 */
class BlockRegistry implements BlockRegistryAPI {
    private blocks = new Map<string, BlockData>();
    private subscribers = new Map<string, Set<EventHandler>>();
    private changeListeners = new Set<() => void>();

    private normalizeId(id: string | number): string {
        return String(id);
    }

    register(id: string | number, data: BlockData): void {
        this.blocks.set(this.normalizeId(id), data);
        this.notifyChangeListeners();
    }

    unregister(id: string | number): void {
        this.blocks.delete(this.normalizeId(id));
        this.notifyChangeListeners();
    }

    get(id: string | number): BlockData | undefined {
        return this.blocks.get(this.normalizeId(id));
    }

    list(): BlockListEntry[] {
        const entries: BlockListEntry[] = [];
        this.blocks.forEach((data, id) => {
            entries.push({
                id,
                type: data.type,
                label: data.label || `${data.type} (${id})`,
            });
        });
        return entries.sort((a, b) => {
            // Sort by numeric ID if possible
            const aNum = parseInt(a.id);
            const bNum = parseInt(b.id);
            if (!isNaN(aNum) && !isNaN(bNum)) {
                return aNum - bNum;
            }
            return a.id.localeCompare(b.id);
        });
    }

    scrollTo(id: string | number, options: ScrollOptions = {}): void {
        const data = this.get(id);
        if (!data?.ref.current) {
            console.warn(`Block with id "${id}" not found or has no ref`);
            return;
        }

        const element = data.ref.current;
        const { behavior = 'smooth', block = 'start', inline = 'nearest', offset = 0 } = options;

        // If offset is provided, we need to manually calculate the position
        if (offset !== 0) {
            const rect = element.getBoundingClientRect();
            const absoluteTop = window.pageYOffset + rect.top;
            window.scrollTo({
                top: absoluteTop - offset,
                behavior,
            });
        } else {
            // Use native scrollIntoView for standard cases
            element.scrollIntoView({
                behavior,
                block,
                inline,
            });
        }
    }

    dispatch(id: string | number, event: string, payload?: any): void {
        const data = this.get(id);
        if (!data) {
            console.warn(`Block with id "${id}" not found`);
            return;
        }

        const callback = data.callbacks?.[event];
        if (callback) {
            callback(payload);
        } else {
            console.warn(`Block "${id}" has no handler for event "${event}"`);
        }
    }

    publish(event: string, payload?: any): void {
        const handlers = this.subscribers.get(event);
        if (handlers) {
            handlers.forEach((handler) => handler(payload));
        }
    }

    subscribe(event: string, handler: EventHandler): () => void {
        if (!this.subscribers.has(event)) {
            this.subscribers.set(event, new Set());
        }
        this.subscribers.get(event)!.add(handler);

        // Return unsubscribe function
        return () => {
            const handlers = this.subscribers.get(event);
            if (handlers) {
                handlers.delete(handler);
                if (handlers.size === 0) {
                    this.subscribers.delete(event);
                }
            }
        };
    }

    // Internal method to notify React components of registry changes
    addChangeListener(listener: () => void): () => void {
        this.changeListeners.add(listener);
        return () => {
            this.changeListeners.delete(listener);
        };
    }

    private notifyChangeListeners(): void {
        this.changeListeners.forEach((listener) => listener());
    }
}

/**
 * React Context for the Block Registry
 */
const BlockRegistryContext = createContext<BlockRegistry | null>(null);

/**
 * Provider component that creates and supplies the registry
 */
export function BlockRegistryProvider({ children }: { children: ReactNode }) {
    const registryRef = useRef<BlockRegistry>(undefined);

    if (!registryRef.current) {
        registryRef.current = new BlockRegistry();
    }

    return <BlockRegistryContext.Provider value={registryRef.current}>{children}</BlockRegistryContext.Provider>;
}

/**
 * Hook to access the block registry
 * @throws Error if used outside of BlockRegistryProvider
 */
export function useBlockRegistry(): BlockRegistryAPI {
    const registry = useContext(BlockRegistryContext);

    if (!registry) {
        throw new Error('useBlockRegistry must be used within a BlockRegistryProvider');
    }

    // Force re-render when registry changes
    const [, forceUpdate] = React.useReducer(() => Math.random(), 0);

    React.useEffect(() => {
        // Subscribe to registry changes
        return (registry as any).addChangeListener(forceUpdate);
    }, [registry]);

    return registry;
}

/**
 * Hook for blocks to register themselves
 * Handles the registration lifecycle automatically
 */
export function useBlockRegistration(
    id: string | number,
    ref: React.RefObject<HTMLElement>,
    options: {
        type: string;
        label?: string;
        callbacks?: BlockData['callbacks'];
    },
) {
    const registry = useBlockRegistry();

    React.useEffect(() => {
        registry.register(id, {
            ref,
            type: options.type,
            label: options.label,
            callbacks: options.callbacks,
        });

        return () => {
            registry.unregister(id);
        };
    }, [id, registry, options.type, options.label]); // Note: callbacks are not in deps to avoid re-registration
}
